// IdCardComponents.tsx
// Pure card rendering components + print HTML builder
// No API calls, no state — just props in, pixels out.

import React, { useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import 'primeicons/primeicons.css';

// ─── Constants ───────────────────────────────────────────────────────────────
export const navy  = '#0e225d';
export const gold  = '#fbb21f';

// CR80 standard ID card: 85.6 × 54 mm
// At 96 dpi: 85.6mm × (96/25.4) = 323px,  54mm × (96/25.4) = 204px
export const CARD_W = 323;
export const CARD_H = 204;

// ─── Types ───────────────────────────────────────────────────────────────────
export interface BatchInfo {
  batch_id:    string;
  batch_name:  string;
  batch_time:  string;
  course_name:  string | null;
  course_title: string | null;
  is_active:   boolean;
  valid_till?: string | null;
}

export interface Student {
  student_id:             string;
  enrollment_id:          string;
  name:                   string;
  dateOfBirth:            string | null;
  gender:                 string | null;
  mobileNumber:           string | null;
  fatherName:             string | null;
  motherName:             string | null;
  district:               string | null;
  state:                  string | null;
  villageTown:            string | null;
  profilePicture:         { cdn_url?: string } | null;
  status:                 string;
  date_of_admission:      string;
  is_registered:          boolean;
  category:               string | null;
  bloodGroup:             string | null;
  emergencyContactName?:  string | null;
  emergencyContactPhone?: string | null;
  validTill?:             string | null;
}

export interface CardProps {
  student:       Student;
  batchInfo:     BatchInfo | null;
  schoolName:    string;
  schoolAddress: string;
  websiteUrl:    string;
  validTill?:    string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
export const getInitials = (name: string) => {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Parse both ISO dates (2002-04-09T...) AND dd/mm/yyyy strings correctly.
 */
export const parseDate = (raw: string | null | undefined): Date | null => {
  if (!raw) return null;
  // dd/mm/yyyy
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

export const formatDate = (raw: string | null | undefined): string => {
  const d = parseDate(raw);
  if (!d) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDOB = formatDate;

// ─── SVG background pattern ───────────────────────────────────────────────────
export const HeaderPattern: React.FC<{ id: string }> = ({ id }) => (
  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg"
    style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
    <defs>
      <pattern id={id} patternUnits="userSpaceOnUse" width="80" height="80">
        <path d="M15 15 L10 20 L15 25" stroke="rgba(251,178,31,0.15)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M25 15 L30 20 L25 25" stroke="rgba(251,178,31,0.15)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="65" cy="25" r="1.5" fill="rgba(251,178,31,0.15)" />
        <circle cx="20" cy="40" r="1.5" fill="rgba(255,255,255,0.1)" />
        <rect x="50" y="15" width="3" height="3" fill="rgba(255,255,255,0.15)" rx="0.5" />
        <rect x="70" y="40" width="3" height="3" fill="rgba(251,178,31,0.2)" rx="0.5" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#${id})`} />
  </svg>
);

// ─── Shared card shell styles ─────────────────────────────────────────────────
export const cardShell: React.CSSProperties = {
  width:        CARD_W,
  height:       CARD_H,
  background:   '#fff',
  borderRadius: 14,
  overflow:     'hidden',
  boxShadow:    '0 12px 40px rgba(14,34,93,0.15), 0 2px 8px rgba(14,34,93,0.08)',
  fontFamily:   "'Plus Jakarta Sans', sans-serif",
  position:     'relative',
  display:      'flex',
  flexDirection:'column',
  flexShrink:   0,
};

// ─── FRONT CARD ───────────────────────────────────────────────────────────────
export const IdCardFront: React.FC<CardProps> = ({
  student, batchInfo, schoolName, schoolAddress, websiteUrl, validTill,
}) => {
  const resolvedValidTill = validTill || student.validTill || batchInfo?.valid_till || null;
  const patId = `fp-${student.student_id}`;

  const rows: [string, string][] = [
    ["Father",      student.fatherName || '-'],
    ["Mother",      student.motherName || '-'],
    ["DOB",         formatDOB(student.dateOfBirth)],
    ["Gender",      student.gender || '-'],
    ["Blood",       student.bloodGroup || '-'],
    ["Category",    student.category || '-'],
    ["Admitted",    formatDate(student.date_of_admission)],
    ["Valid Till",  formatDate(resolvedValidTill)],
  ];

  return (
    <div style={{ ...cardShell, flexDirection: 'row' }}>
      {/* Left navy strip — ~28% of card width */}
      <div style={{
        width: 90, height: '100%', flexShrink: 0,
        background: 'linear-gradient(180deg,#0e225d 0%,#1a3a7a 55%,#234495 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '10px 6px', position: 'relative', zIndex: 2,
      }}>
        <HeaderPattern id={patId} />
        {/* Top accent bar */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${navy},${gold})`, zIndex: 3 }} />

        {/* Logo */}
        <div style={{
          width: 36, height: 36, borderRadius: '50%', background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 1px 5px rgba(0,0,0,0.18)', marginBottom: 4,
          position: 'relative', zIndex: 3, overflow: 'hidden', flexShrink: 0,
        }}>
          <img src="https://hciindia.in/static/images/logo/new-hci-logo.avif" alt="Logo"
            style={{ width: '88%', height: '88%', objectFit: 'contain' }} />
        </div>

        <div style={{ fontSize: 5.5, fontWeight: 800, color: '#fff', textAlign: 'center', lineHeight: 1.3, position: 'relative', zIndex: 3 }}>{schoolName}</div>
        <div style={{ fontSize: 4.5, color: 'rgba(255,255,255,0.6)', textAlign: 'center', marginTop: 2, lineHeight: 1.3, position: 'relative', zIndex: 3 }}>{schoolAddress}</div>

        <div style={{ width: '72%', height: 1, background: 'rgba(255,255,255,0.15)', margin: '5px 0', position: 'relative', zIndex: 3 }} />

        {/* Avatar */}
        <div style={{
          width: 46, height: 46, borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.3)',
          overflow: 'hidden', position: 'relative', zIndex: 3,
          background: gold, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {student.profilePicture?.cdn_url
            ? <img src={student.profilePicture.cdn_url} alt={student.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 16, fontWeight: 800, color: navy }}>{getInitials(student.name)}</span>
          }
          {student.is_registered && (
            <div style={{ position: 'absolute', bottom: 2, right: 2, width: 10, height: 10, background: '#16a34a', borderRadius: '50%', border: '1.5px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="pi pi-check" style={{ fontSize: 4, color: '#fff' }} />
            </div>
          )}
        </div>

        <div style={{ fontSize: 6.5, fontWeight: 800, color: '#fff', textAlign: 'center', marginTop: 4, lineHeight: 1.3, position: 'relative', zIndex: 3 }}>{student.name}</div>

        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, marginTop: 3, background: 'rgba(251,178,31,0.2)', border: '1px solid rgba(251,178,31,0.4)', borderRadius: 3, padding: '1.5px 5px', position: 'relative', zIndex: 3 }}>
          <i className="pi pi-graduation-cap" style={{ fontSize: 5, color: gold }} />
          <span style={{ fontSize: 4.5, fontWeight: 700, color: gold, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Student</span>
        </div>

        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${navy},${gold})`, zIndex: 3 }} />
      </div>

      {/* Right info area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '8px 10px', minWidth: 0 }}>
        {/* Title row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div style={{ minWidth: 0, flex: 1, marginRight: 6 }}>
            <div style={{ fontSize: 5, fontWeight: 700, color: gold, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Student Identity Card</div>
            <div style={{ fontSize: 10, fontWeight: 800, color: navy, letterSpacing: '-0.2px', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{student.name}</div>
          </div>
          <div style={{ padding: '2px 6px', background: 'rgba(14,34,93,0.06)', border: '1px solid rgba(14,34,93,0.12)', borderRadius: 4, fontSize: 6, fontWeight: 700, color: navy, flexShrink: 0 }}>
            {student.enrollment_id || student.student_id.slice(-6).toUpperCase()}
          </div>
        </div>

        {/* Info grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 8px', flex: 1 }}>
          {rows.map(([label, val]) => (
            <div key={label} style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 4.5, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.2px' }}>{label}</div>
              <div style={{ fontSize: 6.5, fontWeight: 700, color: navy, marginTop: 0.5 }}>{val}</div>
            </div>
          ))}
        </div>

        <div style={{ height: 2, background: `linear-gradient(90deg,${navy},${gold},${navy})`, borderRadius: 1, marginTop: 4 }} />
      </div>
    </div>
  );
};

// ─── BACK CARD ────────────────────────────────────────────────────────────────
export const IdCardBack: React.FC<CardProps> = ({
  student, batchInfo, schoolName, schoolAddress, websiteUrl, validTill,
}) => {
  const qrRef = useRef<HTMLCanvasElement>(null);
  const patId = `bp-${student.student_id}`;

  useEffect(() => {
    if (qrRef.current) {
      QRCode.toCanvas(qrRef.current, `https://${websiteUrl}`, {
        width: 54, margin: 1, color: { dark: navy, light: '#ffffff' },
      });
    }
  }, [websiteUrl]);

  const emergencyName  = student.emergencyContactName  || student.fatherName  || student.motherName || '-';
  const emergencyPhone = student.emergencyContactPhone || student.mobileNumber || '-';
  const address = [student.villageTown, student.district, student.state].filter(Boolean).join(', ') || '-';

  return (
    <div style={{ ...cardShell, flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg,#0e225d 0%,#1a3a7a 60%,#234495 100%)',
        padding: '6px 10px', position: 'relative', overflow: 'hidden', flexShrink: 0,
      }}>
        <HeaderPattern id={patId} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${navy},${gold})`, zIndex: 3 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 3 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
              <img src="https://hciindia.in/static/images/logo/new-hci-logo.avif" alt="Logo" style={{ width: 17, height: 17, objectFit: 'contain' }} />
            </div>
            <div>
              <div style={{ fontSize: 7.5, fontWeight: 800, color: '#fff' }}>{schoolName}</div>
              <div style={{ fontSize: 5, color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>{schoolAddress}</div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 5, color: 'rgba(255,255,255,0.6)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Student ID</div>
            <div style={{ fontSize: 7.5, fontWeight: 800, color: gold }}>
              {student.enrollment_id || student.student_id.slice(-6).toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left info */}
        <div style={{ flex: 1, padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 4, borderRight: '1px solid #E8EEF7', overflow: 'hidden' }}>
          {/* Contact */}
          <div>
            <div style={{ fontSize: 5, fontWeight: 700, color: navy, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <i className="pi pi-phone" style={{ color: gold, fontSize: 5 }} /> Contact
            </div>
            {[
              { label: 'Mobile',  val: student.mobileNumber || '-', icon: 'pi-phone' },
              { label: 'Address', val: address,                     icon: 'pi-map-marker' },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                <div style={{ width: 13, height: 13, borderRadius: 3, background: '#E8EEF7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={`pi ${r.icon}`} style={{ fontSize: 6, color: navy }} />
                </div>
                <div>
                  <div style={{ fontSize: 4.5, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase' }}>{r.label}</div>
                  <div style={{ fontSize: 6, fontWeight: 700, color: navy }}>{r.val}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Emergency */}
          <div style={{ background: 'rgba(14,34,93,0.04)', borderRadius: 4, padding: '4px 5px', border: '1px solid rgba(14,34,93,0.08)' }}>
            <div style={{ fontSize: 4.5, fontWeight: 700, color: navy, textTransform: 'uppercase', letterSpacing: '0.2px', marginBottom: 3 }}>Emergency</div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div><div style={{ fontSize: 4.5, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Name</div><div style={{ fontSize: 6, fontWeight: 700, color: navy }}>{emergencyName}</div></div>
              <div style={{ textAlign: 'right' }}><div style={{ fontSize: 4.5, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Phone</div><div style={{ fontSize: 6, fontWeight: 700, color: navy }}>{emergencyPhone}</div></div>
            </div>
            {student.bloodGroup && (
              <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <i className="pi pi-heart-fill" style={{ fontSize: 5, color: '#dc2626' }} />
                <span style={{ fontSize: 4.5, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Blood:</span>
                <span style={{ fontSize: 6, fontWeight: 800, color: '#dc2626' }}>{student.bloodGroup}</span>
              </div>
            )}
          </div>

          {/* Notes */}
          <div style={{ background: 'rgba(251,178,31,0.06)', borderRadius: 3, padding: '3px 5px', border: '1px solid rgba(251,178,31,0.18)' }}>
            {['Property of the institute.', 'Must be carried in premises.', 'Non-transferable.'].map((n, i) => (
              <div key={i} style={{ display: 'flex', gap: 2, marginBottom: 1 }}>
                <div style={{ width: 3, height: 3, borderRadius: '50%', background: gold, marginTop: 2.5, flexShrink: 0 }} />
                <div style={{ fontSize: 5, color: '#6b7280', lineHeight: 1.4 }}>{n}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right QR + signature */}
        <div style={{ width: 76, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', padding: '6px 5px', background: 'rgba(14,34,93,0.02)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 5, fontWeight: 700, color: navy, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 4 }}>Scan to Verify</div>
            <div style={{ width: 48, height: 48, border: '1.5px solid #E8EEF7', borderRadius: 6, overflow: 'hidden', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
              <canvas ref={qrRef} />
            </div>
            <div style={{ fontSize: 5, fontWeight: 700, color: navy, marginTop: 3, letterSpacing: '0.2px' }}>{websiteUrl}</div>
          </div>

          <div style={{ textAlign: 'center', width: '100%', borderTop: '1px solid #E8EEF7', paddingTop: 5 }}>
            <div style={{ fontSize: 15, color: '#1a3a7a', fontStyle: 'italic', letterSpacing: '-1px', fontWeight: 700, lineHeight: 1, marginBottom: 2 }}>ℰ𝓊𝓌</div>
            <div style={{ width: '60%', height: 1, background: navy, margin: '2px auto' }} />
            <div style={{ fontSize: 4.5, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.2px' }}>Issuing Authority</div>
          </div>
        </div>
      </div>

      <div style={{ height: 2, background: `linear-gradient(90deg,${navy},${gold},${navy})`, flexShrink: 0 }} />
    </div>
  );
};

// ─── PRINT HTML BUILDER ───────────────────────────────────────────────────────
// A4 portrait: 210 × 297 mm = ~794 × 1123 px at 96 dpi
// Card: 374 × 220 px → front + back = 748 px wide + 20px gap = 768 px → fits in 794 px
// 4 rows per page: 4 × 54mm = 216mm + 3 × 3mm = 225mm + 12mm padding = 237mm (fits in 297mm)

const printPatternSvg = (id: string) => `
<svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style="position:absolute;inset:0;pointer-events:none">
  <defs>
    <pattern id="${id}" patternUnits="userSpaceOnUse" width="80" height="80">
      <path d="M15 15 L10 20 L15 25" stroke="rgba(251,178,31,0.15)" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M25 15 L30 20 L25 25" stroke="rgba(251,178,31,0.15)" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="65" cy="25" r="1.5" fill="rgba(251,178,31,0.15)"/>
      <circle cx="20" cy="40" r="1.5" fill="rgba(255,255,255,0.1)"/>
      <rect x="50" y="15" width="3" height="3" fill="rgba(255,255,255,0.15)" rx="0.5"/>
      <rect x="70" y="40" width="3" height="3" fill="rgba(251,178,31,0.2)" rx="0.5"/>
    </pattern>
  </defs>
  <rect width="100%" height="100%" fill="url(#${id})"/>
</svg>`;

const fmtPrint = (raw: string | null | undefined): string => {
  if (!raw) return '-';
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const frontCardHTML = (
  s: Student,
  batchInfo: BatchInfo | null,
  schoolName: string,
  schoolAddress: string,
  validTill: string,
): string => {
  const vt  = fmtPrint(validTill || s.validTill || batchInfo?.valid_till || null);
  const pid = `pf-${s.student_id}`;
  const initials = getInitials(s.name);
  const rows: [string, string][] = [
    ['Father',    s.fatherName || '-'],
    ['Mother',    s.motherName || '-'],
    ['DOB',       fmtPrint(s.dateOfBirth)],
    ['Gender',    s.gender || '-'],
    ['Blood',     s.bloodGroup || '-'],
    ['Category',  s.category || '-'],
    ['Admitted',  fmtPrint(s.date_of_admission)],
    ['Valid Till',vt],
  ];
  return `
<div class="card front">
  <div class="left-strip">
    ${printPatternSvg(pid)}
    <div class="accent-bar"></div>
    <div class="logo-circle"><img src="https://hciindia.in/static/images/logo/new-hci-logo.avif" class="logo-img" alt=""/></div>
    <div class="s-name">${schoolName}</div>
    <div class="s-addr">${schoolAddress}</div>
    <div class="divider"></div>
    <div class="avatar">${s.profilePicture?.cdn_url
      ? `<img src="${s.profilePicture.cdn_url}" class="av-img"/>`
      : `<span class="av-init">${initials}</span>`}
    </div>
    <div class="st-name">${s.name}</div>
    <div class="badge">Student</div>
    <div class="bottom-bar"></div>
  </div>
  <div class="right">
    <div class="title-row">
      <div>
        <div class="id-lbl">Student Identity Card</div>
        <div class="id-name">${s.name}</div>
      </div>
      <div class="enroll-no">${s.enrollment_id || s.student_id.slice(-6).toUpperCase()}</div>
    </div>
    <div class="info-grid">
      ${rows.map(([l, v]) => `<div class="info-cell"><div class="lbl">${l}</div><div class="val">${v}</div></div>`).join('')}
    </div>
    <div class="bottom-grad"></div>
  </div>
</div>`;
};

const backCardHTML = (
  s: Student,
  batchInfo: BatchInfo | null,
  schoolName: string,
  schoolAddress: string,
  websiteUrl: string,
): string => {
  const pid          = `pb-${s.student_id}`;
  const initials     = getInitials(s.name);
  const emergName    = s.emergencyContactName  || s.fatherName  || s.motherName || '-';
  const emergPhone   = s.emergencyContactPhone || s.mobileNumber || '-';
  const addr         = [s.villageTown, s.district, s.state].filter(Boolean).join(', ') || '-';
  return `
<div class="card back">
  <div class="back-hdr">
    ${printPatternSvg(pid)}
    <div class="accent-bar"></div>
    <div class="bh-left">
      <div class="logo-sm"><img src="https://hciindia.in/static/images/logo/new-hci-logo.avif" style="width:18px;height:18px;object-fit:contain" alt=""/></div>
      <div><div class="bh-name">${schoolName}</div><div class="bh-addr">${schoolAddress}</div></div>
    </div>
    <div class="bh-right"><div class="bh-lbl">Student ID</div><div class="bh-id">${s.enrollment_id || s.student_id.slice(-6).toUpperCase()}</div></div>
  </div>
  <div class="back-body">
    <div class="back-left">
      <div class="sec-title">Contact Details</div>
      <div class="contact-row"><div class="ic-box">📞</div><div><div class="lbl">Mobile</div><div class="val">${s.mobileNumber || '-'}</div></div></div>
      <div class="contact-row"><div class="ic-box">📍</div><div><div class="lbl">Address</div><div class="val">${addr}</div></div></div>
      <div class="emerg-box">
        <div class="sec-title" style="margin-bottom:4px">Emergency Contact</div>
        <div style="display:flex;justify-content:space-between">
          <div><div class="lbl">Name</div><div class="val">${emergName}</div></div>
          <div style="text-align:right"><div class="lbl">Phone</div><div class="val">${emergPhone}</div></div>
        </div>
        ${s.bloodGroup ? `<div class="blood">❤️ Blood: <strong>${s.bloodGroup}</strong></div>` : ''}
      </div>
      <div class="notes-box">
        <div class="note">• Property of the institute.</div>
        <div class="note">• Must be carried in premises.</div>
        <div class="note">• Non-transferable.</div>
      </div>
    </div>
    <div class="back-right">
      <div class="qr-lbl">Scan to Verify</div>
      <div class="qr-box" id="qr-${s.student_id}"></div>
      <div class="qr-url">${websiteUrl}</div>
      <div class="sig">
        <div class="sig-script">ℰ𝓊𝓌</div>
        <div class="sig-line"></div>
        <div class="sig-lbl">Issuing Authority</div>
      </div>
    </div>
  </div>
  <div class="bottom-grad"></div>
</div>`;
};

export const buildPrintHTML = (
  cards:         Student[],
  batchInfo:     BatchInfo | null,
  schoolName:    string,
  schoolAddress: string,
  websiteUrl:    string,
  validTill:     string,
): string => {
  // 4 cards per A4 page
  let pages = '';
  for (let i = 0; i < cards.length; i += 4) {
    const slice = cards.slice(i, i + 4);
    pages += `<div class="page">${slice.map(s =>
      `<div class="pair">${frontCardHTML(s, batchInfo, schoolName, schoolAddress, validTill)}${backCardHTML(s, batchInfo, schoolName, schoolAddress, websiteUrl)}</div>`
    ).join('')}</div>`;
  }

  const css = `
    *{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    body{font-family:'Plus Jakarta Sans',sans-serif;background:#eef2f9}

    /* ── Screen preview ── */
    .page{
      width:210mm;margin:0 auto;padding:6mm 10mm;
      display:flex;flex-direction:column;gap:3mm;
      page-break-after:always;
      page-break-inside:avoid;
    }
    /* front + back side by side */
    .pair{display:flex;flex-direction:row;gap:6mm;align-items:flex-start}

    /* CR80: 85.6 × 54 mm */
    .card{
      width:85.6mm;height:54mm;
      background:#fff !important;
      border-radius:3mm;overflow:hidden;
      box-shadow:0 3px 12px rgba(14,34,93,0.12);
      position:relative;flex-shrink:0;
    }
    .accent-bar{position:absolute;top:0;left:0;right:0;height:0.6mm;background:linear-gradient(90deg,#0e225d,#fbb21f) !important;z-index:5}
    .bottom-bar,.bottom-grad{height:0.6mm;background:linear-gradient(90deg,#0e225d,#fbb21f,#0e225d) !important}

    /* ── FRONT ── */
    .front{display:flex;flex-direction:row}
    .left-strip{
      width:24mm;height:100%;
      background:linear-gradient(180deg,#0e225d 0%,#1a3a7a 55%,#234495 100%) !important;
      display:flex;flex-direction:column;align-items:center;
      padding:2.5mm 1.5mm;position:relative;flex-shrink:0;
    }
    .logo-circle{width:10mm;height:10mm;border-radius:50%;background:#fff !important;display:flex;align-items:center;justify-content:center;margin-bottom:1mm;box-shadow:0 0.5mm 2mm rgba(0,0,0,0.15);overflow:hidden;position:relative;z-index:3;flex-shrink:0}
    .logo-img{width:88%;height:88%;object-fit:contain}
    .s-name{font-size:4.5pt;font-weight:800;color:#fff;text-align:center;line-height:1.2;position:relative;z-index:3}
    .s-addr{font-size:3.5pt;color:rgba(255,255,255,0.6);text-align:center;margin-top:0.5mm;line-height:1.2;position:relative;z-index:3}
    .divider{width:70%;height:0.3mm;background:rgba(255,255,255,0.15);margin:1.5mm 0;position:relative;z-index:3}
    .avatar{width:13mm;height:13mm;border-radius:50%;border:0.5mm solid rgba(255,255,255,0.3);overflow:hidden;background:#fbb21f !important;display:flex;align-items:center;justify-content:center;position:relative;z-index:3;flex-shrink:0}
    .av-img{width:100%;height:100%;object-fit:cover}
    .av-init{font-size:14pt;font-weight:800;color:#0e225d}
    .st-name{font-size:5pt;font-weight:800;color:#fff;text-align:center;margin-top:1.2mm;line-height:1.2;position:relative;z-index:3}
    .badge{display:inline-flex;align-items:center;gap:0.8mm;background:rgba(251,178,31,0.2) !important;border:0.3mm solid rgba(251,178,31,0.4);border-radius:1mm;padding:0.5mm 1.5mm;font-size:3.5pt;font-weight:700;color:#fbb21f;text-transform:uppercase;margin-top:1mm;position:relative;z-index:3}
    .right{flex:1;display:flex;flex-direction:column;padding:2.5mm 2.5mm}
    .title-row{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:1.5mm}
    .id-lbl{font-size:3.5pt;font-weight:700;color:#fbb21f;text-transform:uppercase;letter-spacing:0.3pt}
    .id-name{font-size:7.5pt;font-weight:800;color:#0e225d;margin-top:0.3mm;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:42mm}
    .enroll-no{padding:0.8mm 1.5mm;background:rgba(14,34,93,0.06) !important;border:0.3mm solid rgba(14,34,93,0.12);border-radius:1mm;font-size:4pt;font-weight:700;color:#0e225d;flex-shrink:0}
    .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:1.2mm 2mm;flex:1}
    .info-cell{display:flex;flex-direction:column}
    .lbl{font-size:3.5pt;font-weight:600;color:#9ca3af;text-transform:uppercase;letter-spacing:0.1pt}
    .val{font-size:5pt;font-weight:700;color:#0e225d;margin-top:0.2mm}
    .bottom-grad{height:0.6mm;background:linear-gradient(90deg,#0e225d,#fbb21f,#0e225d) !important;margin-top:1.5mm}

    /* ── BACK ── */
    .back{display:flex;flex-direction:column}
    .back-hdr{background:linear-gradient(135deg,#0e225d 0%,#1a3a7a 60%,#234495 100%) !important;padding:1.8mm 2.5mm;position:relative;display:flex;justify-content:space-between;align-items:center;flex-shrink:0}
    .bh-left{display:flex;align-items:center;gap:1.5mm;position:relative;z-index:3}
    .logo-sm{width:7mm;height:7mm;border-radius:50%;background:#fff !important;display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0}
    .bh-name{font-size:6pt;font-weight:800;color:#fff}
    .bh-addr{font-size:3.5pt;color:rgba(255,255,255,0.7);margin-top:0.3mm}
    .bh-right{text-align:right;position:relative;z-index:3}
    .bh-lbl{font-size:3.5pt;color:rgba(255,255,255,0.6);font-weight:600;text-transform:uppercase;letter-spacing:0.2pt}
    .bh-id{font-size:6pt;font-weight:800;color:#fbb21f;margin-top:0.3mm}
    .back-body{flex:1;display:flex;overflow:hidden}
    .back-left{flex:1;padding:2mm 2.5mm;display:flex;flex-direction:column;gap:1.5mm;border-right:0.3mm solid #E8EEF7;overflow:hidden}
    .sec-title{font-size:3.8pt;font-weight:700;color:#0e225d;text-transform:uppercase;letter-spacing:0.2pt;margin-bottom:1mm}
    .contact-row{display:flex;align-items:center;gap:1.2mm;margin-bottom:0.8mm}
    .ic-box{width:4mm;height:4mm;border-radius:0.8mm;background:#E8EEF7 !important;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:6pt}
    .emerg-box{background:rgba(14,34,93,0.04) !important;border-radius:1mm;padding:1.2mm 1.5mm;border:0.3mm solid rgba(14,34,93,0.08)}
    .blood{display:flex;align-items:center;gap:0.8mm;margin-top:0.8mm;font-size:3.8pt;color:#dc2626;font-weight:600}
    .notes-box{background:rgba(251,178,31,0.06) !important;border-radius:0.8mm;padding:1mm 1.5mm;border:0.3mm solid rgba(251,178,31,0.18)}
    .note{font-size:3.5pt;color:#6b7280;line-height:1.5;padding-left:2mm;position:relative}
    .note::before{content:"•";position:absolute;left:0;color:#fbb21f}
    .back-right{width:22mm;display:flex;flex-direction:column;align-items:center;justify-content:space-between;padding:2mm 1.5mm;background:rgba(14,34,93,0.02) !important}
    .qr-lbl{font-size:3.5pt;font-weight:700;color:#0e225d;text-transform:uppercase;letter-spacing:0.2pt;margin-bottom:1mm;text-align:center}
    .qr-box{width:16mm;height:16mm;border:0.5mm solid #E8EEF7;border-radius:1.5mm;overflow:hidden;background:#fff !important;display:flex;align-items:center;justify-content:center}
    .qr-url{font-size:3.5pt;font-weight:700;color:#0e225d;margin-top:1mm;letter-spacing:0.1pt;text-align:center}
    .sig{text-align:center;width:100%;border-top:0.3mm solid #E8EEF7;padding-top:1.5mm}
    .sig-script{font-size:12pt;color:#1a3a7a;font-style:italic;letter-spacing:-0.5pt;font-weight:700;line-height:1;margin-bottom:0.5mm}
    .sig-line{width:60%;height:0.3mm;background:#0e225d;margin:0.5mm auto}
    .sig-lbl{font-size:3.5pt;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.2pt}

    @media print{
      html,body{margin:0;padding:0;background:#fff !important}
      .page{padding:6mm 10mm;gap:3mm;width:210mm}
      .pair{gap:6mm}
      .card{box-shadow:none;border:0.3mm solid #ccc}
      @page{size:A4 portrait;margin:0}
    }
  `;

  return `<!DOCTYPE html><html lang="en"><head>
    <meta charset="UTF-8"/>
    <title>Student ID Cards</title>
    <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
    <script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"><\/script>
    <style>${css}</style>
    </head><body>
    ${pages}
    <script>
      window.onload = function() {
        document.querySelectorAll('[id^="qr-"]').forEach(function(el) {
          new QRCode(el, {
            text: "https://${websiteUrl}",
            width: 60, height: 60,
            colorDark: "#0e225d", colorLight: "#ffffff",
            correctLevel: QRCode.CorrectLevel.M
          });
        });
        setTimeout(function(){ window.print(); }, 1200);
      };
    <\/script></body></html>`;
};